import { useState } from 'react';

import { yupResolver } from '@hookform/resolvers/yup';
import { useForm } from 'react-hook-form';
import * as yup from 'yup';

import { CookieManager } from '../../../utils/cookieManager';
import { TokenUtils } from '../../../utils/tokenUtils';
import { useMenu } from 'global/MenuContext';
import { useUser } from 'global/UserContext';
import { useToast } from 'hooks/useToast';
import { apiMain, apiOrchestrator } from 'services/apiService';
import { handleHttpError } from 'utils/errorHandler';
import { findMenuByFormGroupId } from 'utils/formMapper';

export type EventInviteStep = 'email' | 'password' | 'register' | 'select-event';

interface LoginSuccessData {
  access_token: string;
  user_id: string;
  isPasswordReset?: boolean;
  expires_in?: number;
}

interface RegisterResponse {
  uuid: string;
  email: string;
}

interface Event {
  id: number;
  name: string;
  description: string;
  init_datetime: string;
  end_datetime: string;
  is_online: boolean;
  location?: string;
  total_participants?: number;
}

// Email validation schema
const emailSchema = yup.object().shape({
  email: yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
});

// Password validation schema
const passwordSchema = yup.object().shape({
  password: yup
    .string()
    .required('Senha é obrigatória')
    .min(8, 'A senha deve ter no mínimo 8 caracteres'),
});

// Register validation schema
const registerSchema = yup.object().shape({
  firstName: yup.string().required('Nome é obrigatório'),
  lastName: yup.string().required('Sobrenome é obrigatório'),
  email: yup.string().email('E-mail inválido').required('E-mail é obrigatório'),
  phone: yup
    .string()
    .matches(/^\+?[1-9]\d{1,14}$/, 'Número inválido')
    .required('Whatsapp é obrigatório'),
  password: yup
    .string()
    .required('Senha é obrigatória')
    .min(8, 'A senha deve ter no mínimo 8 caracteres')
    .matches(/[A-Z]/, 'A senha deve ter pelo menos uma letra maiúscula')
    .matches(/[a-z]/, 'A senha deve ter pelo menos uma letra minúscula')
    .matches(/\d/, 'A senha deve ter pelo menos um número')
    .matches(/[\W_]/, 'A senha deve ter pelo menos um caractere especial'),
  confirmPassword: yup
    .string()
    .oneOf([yup.ref('password')], 'As senhas devem corresponder')
    .required('Confirmação de senha é obrigatória'),
});

export const useEventInvite = (inviteUuid: string) => {
  const { initializeUserContext } = useUser();
  const { menu } = useMenu();
  const { toastSuccess, toastError, toastInfo } = useToast();

  const [currentStep, setCurrentStep] = useState<EventInviteStep>('email');
  const [loading, setLoading] = useState(false);
  const [userEmail, setUserEmail] = useState('');
  const [upcomingEvents, setUpcomingEvents] = useState<Event[]>([]);
  const [selectedEventId, setSelectedEventId] = useState<number | null>(null);
  const [registeredUserId, setRegisteredUserId] = useState<string | null>(null);

  // Email form
  const emailForm = useForm({
    resolver: yupResolver(emailSchema),
  });

  // Password form
  const passwordForm = useForm({
    resolver: yupResolver(passwordSchema),
  });

  // Register form
  const registerForm = useForm({
    resolver: yupResolver(registerSchema),
  });

  // Check if email exists
  const checkEmailExists = async (email: string): Promise<boolean> => {
    try {
      const response = await apiOrchestrator.post('/auth/check-email', { email });
      return response.data.exists;
    } catch (error) {
      console.error('[EventInvite] Error checking email:', error);
      handleHttpError(error);
      throw error;
    }
  };

  // Handle email submission
  const handleEmailSubmit = async (data: { email: string }) => {
    setLoading(true);
    try {
      setUserEmail(data.email);
      const emailExists = await checkEmailExists(data.email);

      if (emailExists) {
        // Email exists, go to password step
        setCurrentStep('password');
        passwordForm.setValue('password', '');
      } else {
        // Email doesn't exist, go to register step
        setCurrentStep('register');
        registerForm.setValue('email', data.email);
      }
    } catch (error) {
      toastError('Erro ao verificar e-mail. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  // Handle login success
  const handleLoginSuccess = async (data: LoginSuccessData) => {
    try {
      console.log('[EventInvite] Iniciando processamento do login:', {
        hasAccessToken: !!data.access_token,
        hasUserId: !!data.user_id,
        userId: data.user_id,
      });

      if (!data.access_token || !data.user_id) {
        console.error('[EventInvite] Token ou user_id não encontrados na resposta:', data);
        toastError('Erro ao fazer login: dados inválidos');
        return;
      }

      // Save access token in cookie
      CookieManager.setAccessToken(data.access_token);

      // Calculate and save expiration if available
      if (data.expires_in) {
        const expiryTimestamp = TokenUtils.generateExpiryTimestamp(data.expires_in);
        CookieManager.setTokenExpiry(expiryTimestamp);
        console.log('[EventInvite] Expiração do token definida:', new Date(expiryTimestamp));
      }

      // Initialize user context
      console.log('[EventInvite] Chamando initializeUserContext...');
      await initializeUserContext(data.user_id, data.access_token);
      console.log('[EventInvite] Login processado com sucesso!');

      // Set registered user ID and fetch events
      setRegisteredUserId(data.user_id);
      await fetchUpcomingEvents(data.user_id);
      setCurrentStep('select-event');
    } catch (error) {
      console.error('[EventInvite] Erro ao processar login:', error);
      toastError('Erro ao processar login. Por favor, tente novamente.');
    }
  };

  // Handle password submission (login)
  const handlePasswordSubmit = async (data: { password: string }) => {
    setLoading(true);
    try {
      const response = await apiOrchestrator.post('/auth/login', {
        email: userEmail,
        password: data.password,
      });

      console.log('[EventInvite] Login successful:', response.data);

      // Capture headers
      let cognitoUserPool = response.headers['x-cognito-userpool'];
      const organizationUUID = response.headers['x-organization-uuid'];

      // Fallback for Cognito User Pool
      if (!cognitoUserPool) {
        console.warn(
          '[EventInvite] Cognito User Pool not found in headers. Using .env fallback...'
        );
        cognitoUserPool = process.env.REACT_APP_URL_AWS_COGNITO_USER_POOL;
      }

      // Validate headers
      if (!cognitoUserPool || !organizationUUID) {
        console.error('[EventInvite] Required headers not found:', {
          cognitoUserPool: !!cognitoUserPool,
          organizationUUID: !!organizationUUID,
        });
        throw new Error('Required headers not found');
      }

      // Save headers to localStorage
      localStorage.setItem('x-cognito-userpool', cognitoUserPool);
      localStorage.setItem('x-organization-uuid', organizationUUID);

      await handleLoginSuccess(response.data);
    } catch (error: any) {
      console.error('[EventInvite] Error during login:', error);
      if (error.response?.status === 401) {
        toastError('Senha inválida');
      } else if (error.response?.status === 403) {
        toastError('Usuário bloqueado. Por favor, contate o suporte.');
      } else {
        handleHttpError(error);
        toastError('Erro ao fazer login. Por favor, tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Handle registration
  const handleRegisterSubmit = async (data: any) => {
    setLoading(true);
    try {
      const requestData = {
        username: data.email,
        user_attributes: [
          { name: 'given_name', value: data.firstName },
          { name: 'family_name', value: data.lastName },
          { name: 'email', value: data.email },
          { name: 'phone_number', value: `+${data.phone}` },
        ],
        password: data.password,
        message_action: 'SUPPRESS',
        delivery_mediums: ['EMAIL'],
      };

      const response = await apiOrchestrator.post(
        `/auth/${inviteUuid ? `?invite_id=${inviteUuid}` : ''}`,
        requestData
      );

      const registerResponse = response.data as RegisterResponse;
      toastSuccess('Cadastro realizado com sucesso!');

      // Now login the user
      const loginResponse = await apiOrchestrator.post('/auth/login', {
        email: data.email,
        password: data.password,
      });

      // Capture headers
      let cognitoUserPool = loginResponse.headers['x-cognito-userpool'];
      const organizationUUID = loginResponse.headers['x-organization-uuid'];

      if (!cognitoUserPool) {
        cognitoUserPool = process.env.REACT_APP_URL_AWS_COGNITO_USER_POOL;
      }

      if (cognitoUserPool && organizationUUID) {
        localStorage.setItem('x-cognito-userpool', cognitoUserPool);
        localStorage.setItem('x-organization-uuid', organizationUUID);
      }

      await handleLoginSuccess(loginResponse.data);
    } catch (error: any) {
      console.error('[EventInvite] Error during registration:', error);
      if (
        error.response?.data?.detail?.includes('duplicate key value violates unique constraint')
      ) {
        toastError('E-mail já cadastrado. Por favor, faça login.');
        setCurrentStep('password');
      } else {
        handleHttpError(error);
        toastError('Erro ao realizar cadastro. Tente novamente.');
      }
    } finally {
      setLoading(false);
    }
  };

  // Fetch upcoming events
  const fetchUpcomingEvents = async (userId: string) => {
    setLoading(true);
    try {
      const currentDateISO = new Date().toISOString();

      const response = await apiMain.get(`/events/v1/events/next`, {
        params: {
          user_uuid: userId,
          init_datetime: currentDateISO,
        },
      });

      const events = response.data.map((event: any) => ({
        id: event.id,
        name: event.name,
        description: event.description,
        init_datetime: event.init_datetime,
        end_datetime: event.end_datetime,
        is_online: event.is_online,
        location: event.location,
        total_participants: event.total_participants,
      }));

      setUpcomingEvents(events);
    } catch (error) {
      console.error('[EventInvite] Error fetching events:', error);
      toastError('Erro ao carregar eventos.');
    } finally {
      setLoading(false);
    }
  };

  // Check if event is starting soon (within 15 minutes)
  const isEventStartingSoon = (eventDateTime: string): boolean => {
    const eventTime = new Date(eventDateTime);
    const now = new Date();
    const timeDiff = eventTime.getTime() - now.getTime();
    const minutesDiff = timeDiff / (1000 * 60);
    return minutesDiff <= 15 && minutesDiff >= 0;
  };

  // Check if user has completed forms and get the first incomplete form group
  const checkFormCompletion = async (
    userId: string
  ): Promise<{ isCompleted: boolean; firstIncompleteGroupId?: string }> => {
    try {
      const response = await apiMain.get(`/forms/v1/analysis/completion/${userId}`);
      const data = response.data;

      // Check overall completion
      if (data.completion >= 100) {
        return { isCompleted: true };
      }

      console.log('[EventInvite] Data:', data);

      // Find the first incomplete form group
      const firstIncompleteGroup = data.data?.find((group: any) => group.completion < 100);

      return {
        isCompleted: false,
        firstIncompleteGroupId: firstIncompleteGroup?.group_id,
      };
    } catch (error) {
      console.error('[EventInvite] Error checking form completion:', error);
      return { isCompleted: false };
    }
  };

  // Get the path for a form group by finding it in the menu
  const getFormGroupPath = (groupId: string): string => {
    try {
      // Find the menu item that has this form group ID
      const menuItem = findMenuByFormGroupId(menu, groupId);

      console.log('[EventInvite] Menu item:', menuItem);

      if (!menuItem || !menuItem.path) {
        return '/user-profile'; // Fallback to user profile
      }

      return menuItem.path;
    } catch (error) {
      console.error('[EventInvite] Error getting form group path:', error);
      return '/user-profile'; // Fallback to user profile
    }
  };

  // Subscribe to event
  const subscribeToEvent = async () => {
    if (!selectedEventId || !registeredUserId) {
      toastError('Por favor, selecione um evento.');
      return;
    }

    setLoading(true);
    try {
      // Subscribe to the event
      await apiMain.post(
        `/events/v1/user-schedules/respond/${selectedEventId}/${registeredUserId}?status=ACCEPTED`,
        []
      );

      toastSuccess('Inscrito no evento com sucesso!');

      // Find the selected event to check timing
      const selectedEvent = upcomingEvents.find(event => event.id === selectedEventId);

      if (selectedEvent) {
        // Check if event is starting soon (within 15 minutes)
        if (isEventStartingSoon(selectedEvent.init_datetime)) {
          // Event is starting soon, redirect to home
          window.location.href = '/';
        } else {
          // Event is not starting soon, check form completion
          const formCompletion = await checkFormCompletion(registeredUserId);

          if (formCompletion.isCompleted) {
            // Forms completed, redirect to home
            window.location.href = '/';
          } else {
            // Forms not completed, redirect to first incomplete form page
            console.log('[EventInvite] Form completion:', formCompletion);
            if (formCompletion.firstIncompleteGroupId) {
              const formPath = getFormGroupPath(formCompletion.firstIncompleteGroupId);
              window.location.href = formPath;
              console.log('[EventInvite] Form path:', formPath);
            } else {
              // Fallback to user profile if no specific group found
              window.location.href = '/user-profile';
              console.log('[EventInvite] No form group found');
            }
          }
        }
      } else {
        // Fallback to home if event not found
        window.location.href = '/';
      }
    } catch (error) {
      console.error('[EventInvite] Error subscribing to event:', error);
      handleHttpError(error);
      toastError('Erro ao se inscrever no evento. Tente novamente.');
    } finally {
      setLoading(false);
    }
  };

  return {
    currentStep,
    loading,
    userEmail,
    upcomingEvents,
    selectedEventId,
    setSelectedEventId,
    emailForm,
    passwordForm,
    registerForm,
    handleEmailSubmit,
    handlePasswordSubmit,
    handleRegisterSubmit,
    subscribeToEvent,
    setCurrentStep,
  };
};
